// The supplied modules stay native ES modules: their assets resolve via import.meta.url.
export function initBookingPhone(section) {
    const phone = section.querySelector('[data-booking-phone]');
    if (!phone) return () => {};

    const screens = Array.from(phone.querySelectorAll('[data-booking-phone-screen]'));
    const instances = new Map();
    let activeScreen = null;
    let visible = false;

    const sync = () => {
        instances.forEach((state, screen) => {
            if (!state.ready) return;
            const animation = state.phone.animation;
            const shouldPlay = screen === activeScreen && visible && !document.hidden;
            if (shouldPlay && state.restartPending) {
                state.restartPending = false;
                animation.start();
            } else if (shouldPlay && animation.time < animation.duration && !animation.playing) {
                animation.play();
            } else if (!shouldPlay) {
                animation.pause();
            }
        });
    };

    const mount = async (screen) => {
        if (!screen || instances.has(screen)) return;
        const state = { ready: false, phone: null, restartPending: true };
        instances.set(screen, state);
        try {
            const base = new URL(phone.dataset.animationBase, document.baseURI);
            const profile = screen.dataset.animationProfile;
            const [mockup, service] = await Promise.all([
                import(/* webpackIgnore: true */ new URL('mockup/mockup.js', base).href),
                import(/* webpackIgnore: true */ new URL(`${profile}/animation.js`, base).href),
            ]);
            state.phone = mockup.mountIPhoneMockup(screen, {
                profile,
                mountAnimation: service.mountAnimation,
                animationOptions: {
                    autoplay: false,
                    label: screen.dataset.animationLabel,
                },
            });
            await state.phone.ready;
            state.ready = true;
            sync();
        } catch (error) {
            state.phone?.destroy();
            state.failed = true;
            if (activeScreen === screen) {
                screen.hidden = true;
            }
            console.error('Cannot load booking phone animation', error);
        }
    };

    const observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
        if (visible) mount(activeScreen);
        sync();
    }, { threshold: [0, 0.1] });
    observer.observe(phone);

    document.addEventListener('visibilitychange', sync);
    matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
        // Let the supplied controller prepare its new motion state first.
        queueMicrotask(sync);
    });
    window.addEventListener('pagehide', () => {
        instances.forEach((state) => state.phone?.animation?.pause());
    });
    window.addEventListener('pageshow', sync);

    return (tabName) => {
        const nextScreen = screens.find((screen) => screen.dataset.bookingPhoneScreen === tabName) || null;
        if (nextScreen === activeScreen) return;
        activeScreen = nextScreen;
        const state = instances.get(activeScreen);
        if (state) state.restartPending = true;
        screens.forEach((screen) => {
            screen.hidden = screen !== activeScreen || Boolean(instances.get(screen)?.failed);
        });
        if (visible) mount(activeScreen);
        sync();
    };
}
